import { useState, useEffect } from "react";
import { Calendar, Ticket, Car, DollarSign } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { getAllEventTrips } from "@/services/eventPartnerService";
import type { CommunityEvent, EventPartnerTrip } from "@/types/eventPartner";

interface AggregateStatsCardsProps {
  events: CommunityEvent[];
}

export const AggregateStatsCards = ({ events }: AggregateStatsCardsProps) => {
  const [allTrips, setAllTrips] = useState<EventPartnerTrip[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAllTrips = async () => {
      setIsLoading(true);
      try {
        const tripArrays = await Promise.all(
          events.map((event) => getAllEventTrips(event.id))
        );
        setAllTrips(tripArrays.flat());
      } catch (err) {
        console.error("Failed to fetch aggregate trips:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (events.length > 0) {
      fetchAllTrips();
    } else {
      setIsLoading(false);
    }
  }, [events]);

  // Only count BOOKED or later (exclude UNCLAIMED)
  const bookedTrips = allTrips.filter((t) => t.trip_state !== "UNCLAIMED");

  const isShuttle = (name: string) =>
    name.toLowerCase().includes("shuttle");

  const shuttleTickets = bookedTrips.filter((t) =>
    isShuttle(t.user_friendly_name)
  ).length;

  const privateVehicles = bookedTrips.filter(
    (t) => !isShuttle(t.user_friendly_name)
  ).length;

  const totalRevenue = bookedTrips.reduce(
    (sum, t) => sum + (parseFloat(t.rider_price) || 0),
    0
  );

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  const stats = [
    {
      label: "Events",
      value: events.length,
      icon: Calendar,
      color: "text-gray-900",
    },
    {
      label: "Shuttle Tickets Sold",
      value: shuttleTickets,
      icon: Ticket,
      color: "text-gray-900",
    },
    {
      label: "Private Vehicles Sold",
      value: privateVehicles,
      icon: Car,
      color: "text-gray-900",
    },
    {
      label: "Total Revenue",
      value: formatCurrency(totalRevenue),
      icon: DollarSign,
      color: "text-green-600",
    },
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="animate-pulse bg-white border border-gray-200 shadow-sm">
            <CardContent className="p-4">
              <div className="h-4 bg-muted rounded w-24 mb-2" />
              <div className="h-8 bg-muted rounded w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {stats.map((stat) => (
        <Card key={stat.label} className="bg-white border border-gray-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <stat.icon className="h-4 w-4" />
              <span className="text-sm">{stat.label}</span>
            </div>
            <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
